import React from 'react';
import { motion, useInView } from 'framer-motion';
import { useRef } from 'react';
import { ShoppingBag, BarChart3, Terminal, Sparkles } from 'lucide-react';

const LIME = '#D2F34C';
const BLACK = '#000000';

const sourceCards = [
  {
    name: 'Shopify',
    label: 'E-commerce',
    iconBg: '#95BF47',
    Icon: ShoppingBag,
    step: '01',
    fields: [
      { key: 'order_id',  value: '#38291'      },
      { key: 'amount',    value: '$149.99'      },
      { key: 'product',   value: 'Тариф Premium' },
      { key: 'date',      value: '2026-01-15'   },
    ],
  },
  {
    name: 'Google Analytics',
    label: 'Маркетинг',
    iconBg: '#F59E0B',
    Icon: BarChart3,
    step: '02',
    fields: [
      { key: 'source',     value: 'google_cpc' },
      { key: 'campaign',   value: 'brand_q1'   },
      { key: 'landing',    value: '/pricing'   },
      { key: 'session_id', value: 'ga_8x92k'  },
    ],
  },
  {
    name: 'Ваш бэкенд',
    label: 'База продукта',
    iconBg: '#6366F1',
    Icon: Terminal,
    step: '03',
    fields: [
      { key: 'user_id',   value: 'usr_291' },
      { key: 'status',    value: 'active'  },
      { key: 'plan_type', value: 'annual'  },
      { key: 'ltv',       value: '$1,249'  },
    ],
  },
];

const unifiedRows = [
  { label: 'Клиент',      value: 'Анна Петрова'                             },
  { label: 'Заказ',       value: '#38291 — $149.99'                         },
  { label: 'Атрибуция',   value: 'Google Ads → Brand Q1'                   },
  { label: 'Статус',      value: '✓ Активна, годовая', checkmark: true      },
  { label: 'LTV',         value: '$1,249'                                   },
  { label: 'Инсайт',      value: '🔮 Ценный клиент из платного поиска',     insight: true },
];

const MONO = "'JetBrains Mono', ui-monospace, 'Courier New', monospace";

function SourceCard({ card, index }) {
  const { name, label, iconBg, Icon, step, fields } = card;
  return (
    <motion.div
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ delay: index * 0.15, duration: 0.45, ease: 'easeOut' }}
      style={{
        background: '#fff',
        border: `3px solid ${BLACK}`,
        borderRadius: 0,
        overflow: 'hidden',
        flex: '1 1 0',
        minWidth: 0,
        boxShadow: `5px 5px 0 0 ${LIME}`,
        position: 'relative',
      }}
    >
      {/* Colored top accent strip */}
      <div style={{ height: 4, background: iconBg }} />

      {/* Card header */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '12px 14px',
        borderBottom: `2px solid ${BLACK}`,
        background: '#fafafa',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{
            width: 32,
            height: 32,
            background: iconBg,
            border: `2px solid ${BLACK}`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}>
            <Icon size={16} color="#fff" strokeWidth={2.5} />
          </div>
          <div>
            <div style={{ color: BLACK, fontSize: 13, fontWeight: 800, lineHeight: 1.2 }}>{name}</div>
            <div style={{ color: '#888', fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em' }}>{label}</div>
          </div>
        </div>
        {/* Step badge */}
        <div style={{
          background: BLACK,
          color: LIME,
          fontSize: 11,
          fontWeight: 800,
          letterSpacing: '0.05em',
          padding: '3px 8px',
          border: `2px solid ${LIME}`,
          flexShrink: 0,
        }}>
          {step}
        </div>
      </div>

      {/* Data rows */}
      <div>
        {fields.map(({ key, value }, i) => (
          <div
            key={key}
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '9px 14px',
              borderBottom: i < fields.length - 1 ? '1px solid #e8e8e8' : 'none',
              background: i % 2 === 0 ? '#fff' : '#fafafa',
            }}
          >
            <span style={{ fontFamily: MONO, fontSize: 12, color: '#777', fontWeight: 600, letterSpacing: '0.02em' }}>
              {key}
            </span>
            <span style={{ fontFamily: MONO, fontSize: 12, color: BLACK, fontWeight: 800 }}>
              {value}
            </span>
          </div>
        ))}
      </div>
    </motion.div>
  );
}

function FlowingDots() {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true });

  // viewBox 900×130; card centres sit at x=150, 450, 750 and dots fall from each
  // toward the shared centre x=450. preserveAspectRatio="none" keeps x
  // proportional to container width.
  const SVG_H  = 130;
  const DOT    = 7;        // square size px
  const DUR    = 1.9;      // seconds per dot
  const N      = 6;        // dots per lane
  const GAP    = DUR / N;

  // [startX, endX] — y always travels 0 → SVG_H
  const lanes = [
    [150, 450],
    [450, 450],
    [750, 450],
  ];

  return (
    <div ref={ref} style={{ padding: '20px 0 0' }}>
      <svg
        viewBox={`0 0 900 ${SVG_H}`}
        preserveAspectRatio="none"
        style={{ width: '100%', height: SVG_H, display: 'block', overflow: 'visible' }}
      >
        {lanes.map(([sx, ex], li) =>
          Array.from({ length: N }).map((_, di) => (
            <motion.g
              key={`${li}-${di}`}
              initial={{ x: sx, y: -DOT, opacity: 0 }}
              animate={inView ? {
                x:       [sx,   ex],
                y:       [-DOT, SVG_H],
                opacity: [0, 1, 1, 0.6, 0],
              } : {}}
              transition={{
                duration:    DUR,
                delay:       di * GAP,   // stagger within lane; loops restart at delay=0
                repeat:      Infinity,
                repeatDelay: 0,
                ease:        'linear',
                times:       [0, 0.07, 0.72, 0.88, 1],
              }}
            >
              <rect
                x={-DOT / 2} y={0}
                width={DOT}  height={DOT}
                fill={LIME}
                stroke={BLACK} strokeWidth={1.5}
              />
            </motion.g>
          ))
        )}
      </svg>

    </div>
  );
}

function UnifiedCard() {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-80px' });

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 32, scale: 0.97 }}
      animate={inView ? { opacity: 1, y: 0, scale: 1 } : {}}
      transition={{ delay: 1.1, duration: 0.5, ease: 'backOut' }}
      style={{
        maxWidth: 540,
        margin: '0 auto',
        border: `3px solid ${BLACK}`,
        borderRadius: 0,
        overflow: 'hidden',
        boxShadow: `7px 7px 0 0 ${LIME}`,
      }}
    >
      {/* Header */}
      <div style={{
        background: LIME,
        padding: '13px 20px',
        borderBottom: `3px solid ${BLACK}`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 8,
      }}>
        <Sparkles size={15} color={BLACK} />
        <span className="nb-display" style={{ color: BLACK, fontSize: 14, fontWeight: 800, letterSpacing: '0.03em' }}>
          Единая карточка Evolve
        </span>
        <Sparkles size={15} color={BLACK} />
      </div>

      {/* Rows */}
      <div style={{ background: '#fff' }}>
        {unifiedRows.map(({ label, value, checkmark, insight }, i) => (
          <motion.div
            key={label}
            initial={{ opacity: 0, x: -12 }}
            animate={inView ? { opacity: 1, x: 0 } : {}}
            transition={{ delay: 1.25 + i * 0.08, duration: 0.3, ease: 'easeOut' }}
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '11px 20px',
              borderBottom: i < unifiedRows.length - 1 ? `2px solid ${BLACK}` : 'none',
              background: insight ? '#f5f5e8' : '#fff',
            }}
          >
            <span style={{ fontSize: 13, fontWeight: 800, color: BLACK, letterSpacing: '0.01em' }}>
              {label}
            </span>
            <span style={{
              fontSize: 13,
              fontWeight: insight ? 600 : 500,
              color: checkmark ? '#16a34a' : BLACK,
              textAlign: 'right',
              maxWidth: '62%',
            }}>
              {value}
            </span>
          </motion.div>
        ))}
      </div>
    </motion.div>
  );
}

export default function DataFusionSection() {
  return (
    <section style={{ background: '#111111', padding: '96px 24px' }}>
      <div style={{ maxWidth: 1100, margin: '0 auto' }}>

        {/* Badge */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          style={{ display: 'flex', justifyContent: 'center', marginBottom: 28 }}
        >
          <div style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            border: `3px solid ${LIME}`,
            padding: '6px 16px',
            background: 'transparent',
            boxShadow: `3px 3px 0 0 ${LIME}55`,
          }}>
            <Sparkles size={13} color={LIME} />
            <span style={{ color: LIME, fontSize: 12, fontWeight: 700, letterSpacing: '1.5px', textTransform: 'uppercase' }}>
              Автоматическая склейка данных
            </span>
          </div>
        </motion.div>

        {/* Headline */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.05 }}
          style={{ textAlign: 'center', marginBottom: 16, lineHeight: 1.1 }}
        >
          <div className="nb-display" style={{ fontSize: 'clamp(26px, 8.4vw, 52px)', fontWeight: 800, color: '#fff' }}>
            От разрозненных данных —
          </div>
          <div className="nb-display" style={{ fontSize: 'clamp(26px, 8.4vw, 52px)', fontWeight: 800, color: LIME }}>
            к цельной картине
          </div>
        </motion.div>

        {/* Subtitle */}
        <motion.p
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.1 }}
          style={{ textAlign: 'center', color: '#888', fontSize: 16, lineHeight: 1.6, maxWidth: 540, margin: '0 auto 52px' }}
        >
          Мы сами связываем данные из разных источников. Без SQL, без инженеров, без ручной работы.
        </motion.p>

        {/* Cards */}
        <div className="fusion-cards-row" style={{ display: 'flex', gap: 20 }}>
          {sourceCards.map((card, i) => (
            <SourceCard key={card.name} card={card} index={i} />
          ))}
        </div>

        {/* Flowing dots */}
        <FlowingDots />

        {/* Unified card */}
        <UnifiedCard />

      </div>

      <style>{`
        @media (max-width: 768px) {
          .fusion-cards-row { flex-direction: column !important; }
          .fusion-arrows { padding-left: 0 !important; padding-right: 0 !important; justify-content: center !important; gap: 32px !important; }
        }
      `}</style>
    </section>
  );
}
